import { useLazyQuery } from '@apollo/client';
import { sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { branch } from 'src/constants';
import getPortalBanners from 'src/graphql/getPortalBanners';
import getPortalCollecs from 'src/graphql/getPortalCollecs';
import getPortalCategories from 'src/graphql/getPortalCategories';
import getPortalItems from 'src/graphql/getPortalItems';
import getPortalSections from 'src/graphql/getPortalSections';

export function usePortal({ categoryId, productId, q }: any) {
  const [getCats, catData]: any = useLazyQuery(getPortalCategories, {
    fetchPolicy: 'cache-and-network',
    variables: { branch },
  });
  const [getSects, sectData]: any = useLazyQuery(getPortalSections, {
    fetchPolicy: 'cache-and-network',
    variables: { branch },
  });
  const [getItems, itemsData]: any = useLazyQuery(getPortalItems, {
    fetchPolicy: 'cache-and-network',
    variables: { branch },
  });

  const [getBans, banData]: any = useLazyQuery(getPortalBanners, {
    fetchPolicy: 'cache-and-network',
    variables: { branch },
  });

  const [getColc, colData]: any = useLazyQuery(getPortalCollecs, {
    fetchPolicy: 'cache-and-network',
    variables: { branch },
  });

  useEffect(() => {
    getCats();
  }, [getCats]);

  useEffect(() => {
    getSects();
  }, [getSects]);

  useEffect(() => {
    getItems();
  }, [getItems]);

  useEffect(() => {
    getBans();
  }, [getBans]);

  useEffect(() => {
    getColc();
  }, [getColc]);

  const categories = useMemo(() => catData?.data?.getPortalCategories?.data || [], [catData]);
  const sections = useMemo(() => sectData?.data?.getPortalSections?.data || [], [sectData]);
  const items = useMemo(() => itemsData?.data?.getPortalItems?.data || [], [itemsData]);
  const loading = catData.loading || sectData.loading || itemsData.loading;

  const sortedCategories = useMemo(() => sortBy(categories, ['order']), [categories]);

  let products = useMemo(
    () =>
      items?.map((item: any) => {
        const mainimage = item?.photo ? [item?.photo] : [];
        const extraImages = item?.photos || [];
        const images = mainimage.concat(extraImages);
        const thumb = cloudinaryThumb(item?.photo, 300);
        return {
          ...item,
          id: item?._id,
          coverUrl: item?.photo,
          thumb,
          images,
          category: item?.categoryName,
          description: item?.desc,
          descriptionAr: item?.descAr,
          colors: [item?.color || '#000000'],
          sizes: [item?.size],
          newLabel: item?.newLabel || null,
          saleLabel: item?.saleLabel || null,
        };
      }),
    [items]
  );
  // if q search products name nameAr use lodash and filter
  if (q) {
    const qSearch = q.toLowerCase();
    products = products.filter(
      (product: any) =>
        product?.name?.toLowerCase().includes(qSearch) ||
        product?.nameAr?.toLowerCase().includes(qSearch)
    );
  }

  const banners = useMemo(() => banData?.data?.getPortalBanners?.data || [], [banData]);
  const collections = useMemo(() => {
    try {
      const collecs = colData?.data?.getPortalCollecs?.data || [];
      return sortBy(collecs, ['order']);
    } catch (error) {
      console.error('Error processing collections in use-portal:', error);
      return [];
    }
  }, [colData]);
  const banner = useMemo(() => {
    const banndata = banners?.[0];
    const list = banndata?.data?.map((item: any, index: any) => ({
      id: index,
      title: banndata?.name,
      coverUrl: item?.desktop,
      description: banndata?.name,
    }));
    return list;
  }, [banners]);

  const product = useMemo(
    () => (productId ? products?.find((item: any) => item?.id === productId) : null),
    [productId, products]
  );

  const relatedProducts = useMemo(
    () => products.filter((prod: any) => prod?.categoryId === product?.categoryId),
    [products, product]
  );

  return {
    categories: sortedCategories,
    cteegoriesOptions: sortedCategories.map((category: any) => category?.name),
    sections,
    items: categoryId ? items?.filter((item: any) => item?.categoryId === categoryId) : items,
    loading,
    products: categoryId
      ? products?.filter((prod: any) => prod?.categoryId === categoryId)
      : products,
    product,
    relatedProducts,
    banner,
    collections,
  };
}

export function cloudinaryThumb(url: string, width = 200) {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', `/upload/w_${width}/`);
}
